<template>
  <div class="py-300 action-required">
    <div class="title-container">
      <h3 class="title">
        <BIcon
          rounded
          class="mr-100"
        >
          tv_options_edit_channels
        </BIcon>
        <span>{{ $t('actionRequired.title') }}</span>
      </h3>
      <div class="number-of-items">
        {{ numberOfItems }}
      </div>
      <SelectUser
        v-if="authorize(['owner'])"
        v-model="selectedOwnerId"
        :placeholder="$t('actionRequired.filter.ownerPlaceholder')"
        class="owner-select"
        clearable
        only-active-users
        @update:model-value="handleOwnerChange"
      />
    </div>
    <BCard
      v-loading="isLoading"
      class="action-required-card"
    >
      <BLayout
        align-start
        class="action-required-card-header"
      >
        <div class="content">
          {{ $t('actionRequired.nextActionSuggestion') }}
        </div>
        <div class="content">
          {{ $t('actionRequired.callFeedback') }}
        </div>
      </BLayout>
      <div
        class="action-required-card-body"
        :class="{ 'fixed-height': shouldUseFixedHeight }"
      >
        <template v-if="!isLoading">
          <div class="content">
            <template v-if="nextActionSuggestions.length === 0">
              <div class="empty-box">
                {{ $t('sisense.empty') }}
              </div>
            </template>
            <template v-else>
              <NextActionSuggestion
                v-for="suggestion in nextActionSuggestions"
                :key="suggestion.id"
                :next-action="suggestion"
              />
            </template>
          </div>
          <div class="content">
            <template v-if="callFeedbacks.length === 0">
              <div class="empty-box">
                {{ $t('sisense.empty') }}
              </div>
            </template>
            <template v-else>
              <CallFeedback
                v-for="feedback in callFeedbacks"
                :key="feedback.callResultId"
                :call-feedback="feedback"
              />
            </template>
          </div>
        </template>
      </div>
    </BCard>
    <div
      v-if="expandable && !isLoading"
      class="expand-button"
      @click="toggleExpanded"
    >
      <BBtn
        variant="outlined"
        size="mini"
      >
        <BIcon
          class="expand-icon"
          size="small"
        >
          {{ isExpanded ? 'unfold_less' : 'unfold_more' }}
        </BIcon>
        {{ isExpanded ? $t('general.listCollapse.collapse') : $t('general.listCollapse.expand') }}
      </BBtn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NextActionSuggestion as TNextActionSuggestion,
  CallFeedback as TCallFeedback,
} from '@/api/openapi';
import { CallFeedbackApiService } from '@/api/user/resources/call_feedback';
import { NextActionSuggestionApiService } from '@/api/user/resources/next_action_suggestion';
import { useCurrentUserProvider } from '@/composable/current-user';
import { useAlert } from '@/composable/message-dialog';
import { useCurrentUser } from '@/composable/user/user/users';
import { useWait } from '@/composable/vue-wait';
import SelectUser from '@/plugins/biscuet-materials/components/molecules/SelectUser.vue';
import { startOf, subtract, formatISO8601Date } from '@/utils/date-time';
import CallFeedback from './CallFeedback.vue';
import NextActionSuggestion from './NextActionSuggestion.vue';

const { doActionWithWait, wait } = useWait();
const { currentUser, fetchCurrentUserPromise } = useCurrentUser();
const { alert } = useAlert();

const { t } = useI18n();
const { inject: injectCurrentUser } = useCurrentUserProvider();
const { authorize } = injectCurrentUser();
const nextActionSuggestions = ref<TNextActionSuggestion[]>([]);
const totalCount = ref<number>(0);
const selectedOwnerId = ref<number | undefined>();
const isExpanded = ref<boolean>(false);
const callFeedbacks = ref<TCallFeedback[]>([]);

const NUMBER_OF_COLLAPSED_ITEMS = 2; // 折りたたみ時の表示件数
const NUMBER_OF_EXPANDED_ITEMS = 4; // 展開時の表示件数

const isLoading = computed(() => {
  return wait.is('fetchNextActionSuggestions');
});
const numberOfItems = computed(() => {
  if (totalCount.value > nextActionSuggestions.value.length) {
    return `${nextActionSuggestions.value.length} / ${t('general.numberOf', { number: totalCount.value })}`;
  }
  return t('general.numberOf', { number: nextActionSuggestions.value.length });
});

// 開閉機能
const expandable = computed(() => {
  return nextActionSuggestions.value.length > NUMBER_OF_COLLAPSED_ITEMS;
});
const numberOfMaxVisibleItems = computed(() => {
  return isExpanded.value ? NUMBER_OF_EXPANDED_ITEMS : NUMBER_OF_COLLAPSED_ITEMS;
});
const shouldUseFixedHeight = computed(() => {
  return nextActionSuggestions.value.length > numberOfMaxVisibleItems.value;
});

onBeforeMount(async () => {
  await fetchCurrentUserPromise;
  // デフォルトでログインユーザーを選択
  selectedOwnerId.value = currentUser.value?.id;
  await fetchNextActionSuggestions();
  await fetchCallFeedbacks();
});

// データが変更されたときは開閉状態をリセット
watch(nextActionSuggestions, () => {
  isExpanded.value = false;
});

const fetchNextActionSuggestions = async () => {
  await doActionWithWait('fetchNextActionSuggestions', async () => {
    const api = new NextActionSuggestionApiService();
    const { data } = await api.getNextActionSuggestions({
      request: {
        ownerId: selectedOwnerId.value,
      },
    });
    nextActionSuggestions.value = data.nextActionSuggestions || [];
    totalCount.value = data.meta?.totalCount || 0;
  });
};

const fetchCallFeedbacks = async () => {
  await doActionWithWait('fetchCallFeedbacks', async () => {
    const api = new CallFeedbackApiService();
    const { data } = await api.getCallFeedbacks({
      request: {
        userId: selectedOwnerId.value,
        unconfirmed: true,
      },
      errorHandlers: {
        400: (error) => {
          alert({
            title: t('general.error'),
            message: error.response.data.errorMessage,
          });
        },
      },
    });
    callFeedbacks.value = data.callFeedbacks || [];
    totalCount.value = data.meta?.totalCount || 0;
  });
};

const handleOwnerChange = async () => {
  await fetchNextActionSuggestions();
  await fetchCallFeedbacks();
};

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};
</script>

<style lang="scss" scoped>
$item-height: 136px;
.action-required {
  .title-container {
    @include m-flex-align-center;
    .title {
      @include m-flex-align-center;
      margin-right: $basespace-400;
    }
    .number-of-items {
      @include m-flex-align-center;
      margin-right: $basespace-300;
      font-weight: $font-weight-bold;
    }
    .owner-select {
      width: 200px;
    }
  }

  .expand-button {
    @include m-flex-align-center;
    @include m-flex-center;
    padding-top: $basespace-200;

    .expand-icon {
      margin-left: $basespace-50;
      transition: transform 0.3s ease-in-out;
    }
  }

  .action-required-card {
    padding: 0;
    margin-top: $basespace-200;

    .action-required-card-header {
      border-bottom: 1px solid $bdcolor-base;
      font-weight: $font-weight-bold;
      color: $textcolor-light;

      .content {
        flex: 1;
        padding: $basespace-100 $basespace-300;
      }
    }
    .action-required-card-body {
      display: flex;

      .content {
        flex: 1;
        overflow-y: auto;
        &:first-child {
          border-right: 1px solid $bdcolor-base;
        }
      }
      .empty-box {
        padding: $basespace-300;
      }
      &.fixed-height {
        max-height: calc($item-height * v-bind(numberOfMaxVisibleItems) + $basespace-100);
        transition: max-height 0.3s ease-in-out;
      }
    }
  }
}
</style>
